import {
  getReportingAdminClaimStatus,
  getReportingManagerPrivilegeClaimStatus
} from '@opengov/app-core/claims/selectors';
import { createSelector } from '@opengov/compose-app/reselect';

// Authoring is authorized by the entity-scoped reporting claims, never by the v1 session's
// `abilities.report`. That payload describes the user's *primary* entity, so a guest invited into
// an entitled entity always reads `create: false` there and lost the gallery's controls even where
// the service would have allowed them (BUD-18633 follow-up). `verifyRequestedEntity` awaits
// `fetchClaims` for the entity in the URL before any route renders, so these are already loaded.
//
// Manager-or-Creator mirrors the service's authoring authority for both reports and folders:
// `ResourcePolicy` clears REPORTING_MANAGER_PRIVILEGE ahead of every resource policy, and
// `ReportPolicy`/`FolderPolicy` admit REPORTING_ADMIN_CLAIM for Create. Whether a *particular*
// report may be duplicated or deleted remains a per-resource check on the server.
export const selectCanCreateReport = createSelector(
  getReportingManagerPrivilegeClaimStatus,
  getReportingAdminClaimStatus,
  (hasManagerPrivilege, hasCreatorClaim): boolean => hasManagerPrivilege || hasCreatorClaim
);

// One authoring decision drives every gallery and folder control, so they cannot drift apart.
// Shared by the reports gallery and the report builder's folder picker, both of which hand the
// result to `FoldersProvider` — reselect memoizes it, so the identity is stable and reading it
// on each render does not churn the folder context.
export const selectReportAuthoringAbilities = createSelector(selectCanCreateReport, (canAuthor) => ({
  create: canAuthor,
  duplicate: canAuthor,
  move: canAuthor,
  rename: canAuthor,
  delete: canAuthor
}));
